import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { kebabCase } from 'lodash';
import React, { useMemo, useState } from 'react';
import { ButtonDropdown, DropdownItem, DropdownToggle, DropdownMenu } from 'reactstrap';
import PropTypes from 'prop-types';
import { checkPermission, makeResource } from '../../../behaviors/src/lib/permissions';
import Icon from '../common/containers/icon';
import CabinetActionModal from './cabinetActionModal';
import FormModal from './formModal';
const CabinetActionsMenu = _ref => {
  let actionsMenuItems = _ref.actionsMenuItems,
    _ref$availableFormTyp = _ref.availableFormTypeIdentifiers,
    availableFormTypeIdentifiers = _ref$availableFormTyp === void 0 ? [] : _ref$availableFormTyp,
    onBulkAction = _ref.onBulkAction,
    _ref$cabinetIdentifie = _ref.cabinetIdentifier,
    cabinetIdentifier = _ref$cabinetIdentifie === void 0 ? '' : _ref$cabinetIdentifie,
    _ref$metaDeleted = _ref.metaDeleted,
    metaDeleted = _ref$metaDeleted === void 0 ? 0 : _ref$metaDeleted,
    _ref$permissions = _ref.permissions,
    permissions = _ref$permissions === void 0 ? {} : _ref$permissions,
    _ref$selected = _ref.selected,
    selected = _ref$selected === void 0 ? [] : _ref$selected,
    _ref$selectedFormType = _ref.selectedFormTypeIdentifiers,
    selectedFormTypeIdentifiers = _ref$selectedFormType === void 0 ? [] : _ref$selectedFormType,
    _ref$user = _ref.user,
    user = _ref$user === void 0 ? {} : _ref$user;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedAction = _useState4[0],
    setSelectedAction = _useState4[1];
  const toggle = () => {
    setOpen(!open);
  };

  // Filter actions based on metaDeleted state
  // - Show 'Delete' only when viewing non-deleted forms (metaDeleted !== 1)
  // - Show 'Undelete' only when viewing deleted forms (metaDeleted === 1)
  const filteredActionsMenuItems = useMemo(() => actionsMenuItems.filter(item => {
    var _item$action;
    const actionType = (_item$action = item.action) === null || _item$action === void 0 ? void 0 : _item$action.type;
    if (actionType === 'bulkDelete') {
      return metaDeleted !== 1;
    }
    if (actionType === 'bulkUndelete') {
      return metaDeleted === 1;
    }
    return true;
  }), [actionsMenuItems, metaDeleted]);

  /**
   *  Send the bulk action to the action layer
   */
  const handleButtonSelect = currentAction => {
    onBulkAction(cabinetIdentifier, selected, currentAction);
    setSelectedAction(null);
  };

  /**
   *  Handle the action menu item selection. If an `action` property
   *  is set handle the action using CabinetActionModal. If an
   *  `actions` property is set, run these as behaviors
   */
  const handleSelectedMenuItem = async currentMenuItem => {
    const action = currentMenuItem.action,
      actions = currentMenuItem.actions;
    if (!action && !actions) {
      // eslint-disable-next-line no-console
      console.error('No action or actions property found in the menu item');
      return;
    }
    if (action) {
      setSelectedAction(action);
    } else if (actions) {
      // these should be behaviors, so call the onBulkAction function
      // to run these within the redux actions layer
      await onBulkAction(cabinetIdentifier, selected, actions);
    }
  };
  const isActionEnabledForSelection = function isActionEnabledForSelection(actionPermission) {
    let requiresSelection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    if (!actionPermission) {
      return false;
    }
    const formPermissionPrefix = 'allow_form_';
    const normalizedPermission = actionPermission.startsWith(formPermissionPrefix) ? actionPermission.substring(formPermissionPrefix.length) : actionPermission.substring(actionPermission.lastIndexOf('_') + 1);
    const formTypeIdentifiers = requiresSelection ? selectedFormTypeIdentifiers : availableFormTypeIdentifiers;
    if (requiresSelection && !selected.length) {
      return false;
    }
    if (!formTypeIdentifiers.length) {
      return false;
    }
    if (requiresSelection) {
      return formTypeIdentifiers.every(ftIdentfier => checkPermission(makeResource('form', ftIdentfier), normalizedPermission, permissions, user));
    }
    return formTypeIdentifiers.some(ftIdentfier => checkPermission(makeResource('form', ftIdentfier), normalizedPermission, permissions, user));
  };
  const selectedCount = useMemo(() => {
    const selectedSize = new Set(selected).size;
    return selectedSize > 0 ? "(".concat(selectedSize, ")") : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.length]);
  const hasNoSelectionActions = useMemo(() => filteredActionsMenuItems.some(item => item.requiresSelection === false), [filteredActionsMenuItems]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ButtonDropdown, {
    id: "cabinetActionsMenu",
    "data-testid": "t-cam-button",
    isOpen: open,
    toggle: toggle,
    size: "sm"
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    disabled: selected.length === 0 && !hasNoSelectionActions,
    caret: true
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    glyph: "bolt"
  }), " Actions ", selectedCount)), /*#__PURE__*/React.createElement(DropdownMenu, null, filteredActionsMenuItems.map(actionMenuItem => /*#__PURE__*/React.createElement(DropdownItem, {
    key: actionMenuItem.title,
    "data-testid": "t-cam-button-".concat(kebabCase(actionMenuItem.title)),
    className: "t-cam-button-".concat(kebabCase(actionMenuItem.title)),
    onClick: () => handleSelectedMenuItem(actionMenuItem),
    disabled: !isActionEnabledForSelection(actionMenuItem.permission, actionMenuItem.requiresSelection !== false),
    style: kebabCase(actionMenuItem.title) === 'delete' ? {
      color: 'red'
    } : {}
  }, /*#__PURE__*/React.createElement(Icon, {
    style: {
      width: '10%',
      marginRight: '5px'
    },
    glyph: actionMenuItem.icon
  }), " ", actionMenuItem.title)), selectedAction && /*#__PURE__*/React.createElement(CabinetActionModal, {
    selectedAction: selectedAction,
    shouldPluralizeText: selected.length > 1,
    onClose: () => setSelectedAction(null),
    onConfirm: () => handleButtonSelect(selectedAction),
    cabinetIdentifier: cabinetIdentifier
  }))), /*#__PURE__*/React.createElement(FormModal, null));
};
CabinetActionsMenu.propTypes = {
  actionsMenuItems: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    icon: PropTypes.string
  })).isRequired,
  availableFormTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  cabinetIdentifier: PropTypes.string,
  metaDeleted: PropTypes.number,
  onBulkAction: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}),
  selectedFormTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.arrayOf(PropTypes.string),
  user: PropTypes.shape({})
};
export default CabinetActionsMenu;